import CoreBase from '../../services/engines/CoreBase';
import { getConnectorFixStrategy } from './ConnectorFix/ConnectorFixStrategyRegistry';
import outputMsgEngine from '../../Widgets/OutputMsgWidget/Core/OutputMsgEngine';
import { DEBUG_LOGGING_ENABLED } from '../../services/utils/debugMode';
import { findById } from './UmlCanvasEngineHelper';
import { resolveGeometryShapeIds } from './UmlConnectorResolver.js';

// Maps every shape id to the id of its nearest COLLAPSED ancestor package, if
// any — used by resolveCollapsedPackageAnchors to redirect a connector
// endpoint hidden inside a collapsed package to the package itself.
function buildCollapsedAncestorMap(shapes) {
    const map = new Map();
    function walk(shapeList, ancestorId) {
        shapeList.forEach((shape) => {
            if (ancestorId) {
                map.set(shape.id, ancestorId);
            }
            if (Array.isArray(shape.children)) {
                walk(shape.children, shape.collapsed ? shape.id : ancestorId);
            }
        });
    }
    walk(shapes, null);
    return map;
}

/**
 * Owns connector state for the UML canvas — pub/sub singleton, single source
 * of truth for this.connectors. Split off from UmlCanvasEngine (shapes/
 * containment) for single-responsibility, same pattern as UmlViewportEngine/
 * UmlSelectionEngine. Extends CoreBase for logging only.
 */
class UmlConnectorEngine extends CoreBase {
    _name = 'UmlConnectorEngine';

    constructor() {
        super();
        this.connectors = []; // UmlConnector[]
        this.listeners = [];
    }

    subscribe(callback) {
        this.listeners.push(callback);
        return function unsubscribe() {
            this.listeners = this.listeners.filter(function filterOutListener(listener) {
                return listener !== callback;
            });
        }.bind(this);
    }

    notify() {
        this.listeners.forEach(function callListener(listener) {
            listener();
        });
    }

    /**
     * Replaces the full connector list (schema load).
     * @param {Array} connectors
     */
    loadConnectors(connectors) {
        this.connectors = connectors;
        this.notify();
    }

    /**
     * Removes every connector touching any of the given shape ids — called by
     * UmlCanvasEngine.removeShape.
     *
     * Takes an ARRAY, not a single shapeId: a deleted package's children
     * (UmlCompositeShape.children) are nested INSIDE the shape object, but
     * connectors are not — they live here, as a flat list, referencing a
     * shape only by id. Filtering on just the package's own id used to leave
     * connectors attached to a shape NESTED inside that package dangling,
     * pointing at an id that no longer exists. Callers must pass every id in
     * the deleted subtree (see collectShapeIdsDeep in UmlCanvasEngineHelper).
     * @param {string[]} shapeIds
     * @returns {string[]} ids of the connectors actually removed — callers
     *   (UmlCanvasEngine.removeShape) need these to also strip them from
     *   viewConfig.userHiddenItems, or a hidden connector deleted this way
     *   leaves a dead id behind forever (see UmlCanvasEngine.removeFromHiddenItems).
     */
    removeConnectorsForShapeIds(shapeIds) {
        const removedIds = this.connectors
            .filter(function matchRemoved(connector) {
                return shapeIds.includes(connector.fromShapeId) || shapeIds.includes(connector.toShapeId);
            })
            .map(function toId(connector) {
                return connector.id;
            });
        this.connectors = this.connectors.filter(function filterOutConnector(connector) {
            return !shapeIds.includes(connector.fromShapeId) && !shapeIds.includes(connector.toShapeId);
        });
        this.notify();
        return removedIds;
    }

    addConnector(connector) {
        this.connectors.push(connector);
        this.notify();
    }

    removeConnector(connectorId) {
        this.connectors = this.connectors.filter(function filterOutConnector(connector) {
            return connector.id !== connectorId;
        });
        this.notify();
    }

    getConnector(connectorId) {
        return findById(this.connectors, connectorId);
    }

    // Detaches one end of an existing connector from its current shape and attaches it to another.
    // Guards against a self-loop: the two ends must never resolve to the same shapeId
    // (found 2026-07-20 — a reattach drop landing on the connector's OTHER, fixed end
    // silently produced a degenerate connector with fromShapeId === toShapeId).
    reattachConnectorEnd(connectorId, variant, newShapeId) {
        const connector = this.getConnector(connectorId);
        if (!connector) {
            return;
        }
        const otherShapeId = variant === 'start' ? connector.toShapeId : connector.fromShapeId;
        if (newShapeId === otherShapeId) {
            return;
        }
        if (variant === 'start') {
            connector.fromShapeId = newShapeId;
            connector.fromAnchorPoint = null;
        } else {
            connector.toShapeId = newShapeId;
            connector.toAnchorPoint = null;
        }
        this.notify();
    }

    // Manual border-anchor override for one end of a connector. null resets to auto.
    setConnectorAnchor(connectorId, variant, point) {
        const connector = this.getConnector(connectorId);
        if (!connector) {
            return;
        }
        if (variant === 'start') {
            connector.fromAnchorPoint = point;
        } else {
            connector.toAnchorPoint = point;
        }
        this.notify();
    }

    // Appends a waypoint to an existing connector.
    addConnectorPoint(connectorId, point) {
        const connector = this.getConnector(connectorId);
        if (!connector) {
            return;
        }
        connector.addPoint(point);
        this.notify();
    }

    // Inserts a waypoint at a specific index (nearest-segment lookup done by caller).
    insertConnectorPoint(connectorId, index, point) {
        const connector = this.getConnector(connectorId);
        if (!connector) {
            return;
        }
        connector.insertPoint(index, point);
        this.notify();
    }

    // Removes a waypoint from a connector.
    removeConnectorPoint(connectorId, pointId) {
        const connector = this.getConnector(connectorId);
        if (!connector) {
            return;
        }
        connector.removePoint(pointId);
        this.notify();
    }

    /**
     * Applies a connector-fix strategy to every connector, mutating them in place.
     * @param {string} strategyKey - key from ConnectorFixStrategyRegistry
     * @param {Function} getShapeById - shape lookup, injected by the caller (UmlCanvasEngine owns shapes)
     * @param {Array} visibleConnectors - resolveInterfaceReduction(...).visibleConnectors, used to
     *   resolve the correct (possibly rewired-to-implementer) shape ids per connector — same reasoning
     *   as UmlConnectorResolver.resolveGeometryShapeIds, see useConnectorKeyboardShortcuts. Optional:
     *   falls back to the connector's own shape ids when omitted (e.g. no reduction in play).
     */
    straightenAllConnectors(strategyKey = 'straightenExisting', getShapeById, visibleConnectors = []) {
        const strategy = getConnectorFixStrategy(strategyKey);
        if (DEBUG_LOGGING_ENABLED) {
            outputMsgEngine.addMsg('Debug', 'log', `Fix Lines | strategy: ${strategyKey} | found: ${!!strategy} | connectors: ${this.connectors.length}`);
        }
        if (!strategy) return;
        this.connectors.forEach((connector) => {
            const { fromShapeId, toShapeId } = resolveGeometryShapeIds(connector, visibleConnectors);
            const fromShape = getShapeById(fromShapeId);
            const toShape = getShapeById(toShapeId);
            if (!fromShape || !toShape) {
                if (DEBUG_LOGGING_ENABLED) {
                    outputMsgEngine.addMsg('Debug', 'error', `${connector.id.slice(0, 8)} | Fix Lines | skipped | fromShape or toShape not found`);
                }
                return;
            }
            const before = JSON.stringify({ from: connector.fromAnchorPoint, to: connector.toAnchorPoint });
            try {
                strategy.run(connector, fromShape, toShape);
                if (DEBUG_LOGGING_ENABLED) {
                    const after = JSON.stringify({ from: connector.fromAnchorPoint, to: connector.toAnchorPoint });
                    outputMsgEngine.addMsg('Debug', 'log', `${connector.id.slice(0, 8)} | Fix Lines | before: ${before} | after: ${after}`);
                }
            } catch (error) {
                outputMsgEngine.addMsg('Debug', 'error', `${connector.id.slice(0, 8)} | Fix Lines | threw | ${error.message}`);
            }
        });
        this.notify();
    }

    /**
     * Presentation-only view of connectors accounting for collapsed packages.
     * An endpoint hidden inside a collapsed package is rewired to point at
     * that package instead (nearest collapsed ancestor, for nested packages).
     * A connector whose BOTH endpoints resolve to the same collapsed package
     * is dropped entirely — an internal connector would otherwise draw as a
     * self-loop on the package. Never mutates the real connectors; chained
     * into the same shapes/connectors pipeline as resolveUserHiddenItems /
     * resolveInterfaceReduction (see UmlCanvas.jsx).
     * @param {object[]} shapes - visible shapes (top-level, incl. package children)
     * @param {object[]} connectors - visible connectors
     * @returns {object[]}
     */
    resolveCollapsedPackageAnchors(shapes, connectors) {
        const ancestorMap = buildCollapsedAncestorMap(shapes);
        if (ancestorMap.size === 0) return connectors;

        return connectors
            .map((connector) => {
                const fromAncestor = ancestorMap.get(connector.fromShapeId);
                const toAncestor = ancestorMap.get(connector.toShapeId);
                if (!fromAncestor && !toAncestor) return connector;
                if (fromAncestor && fromAncestor === toAncestor) return null;
                const clone = Object.create(Object.getPrototypeOf(connector));
                Object.assign(clone, connector, {
                    fromShapeId: fromAncestor || connector.fromShapeId,
                    toShapeId: toAncestor || connector.toShapeId,
                });
                return clone;
            })
            .filter(Boolean);
    }
}

const umlConnectorEngine = new UmlConnectorEngine();
export default umlConnectorEngine;
